import React, { useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import { useTranslation } from "react-i18next";
import { LivePal, GearPal } from "../types/Pal";
import L from "leaflet";
import PalPopup from "./PalPopup/PalPopup";
import GearPalClusterPostalCodePopup from "./GearPalClusterPostalCodePopup";
import GearPalClusterMarker from "./GearPalClusterMarker";
import MapLegend from "./MapLegend";
import { DrumIconRaw, VenuesIconRaw } from "../ui/icons";

// Icone personalizzate per i diversi tipi di Pal
const createCustomIcon = (type: "LivePal" | "GearPal" | "GearPalCluster") =>
  L.divIcon({
    className: `custom-div-icon ${type.toLowerCase()}`,
    html: `<div class="custom-marker-pin ${type.toLowerCase()}-pin">
    ${
      type === "LivePal" ? VenuesIconRaw(18, "white") : DrumIconRaw(16, "white")
    }
    </div>`,
    iconSize: [50, 50],
    iconAnchor: [25, 25],
    popupAnchor: [0, -25],
  });

const livePalIcon = createCustomIcon("LivePal");
const gearPalIcon = createCustomIcon("GearPal");
const gearPalClusterIcon = createCustomIcon("GearPalCluster");

interface MapProps {
  pals: (LivePal | GearPal)[];
}

const MapWrapper: React.FC<MapProps & { onPalUpdate: () => void }> = ({
  pals,
  onPalUpdate,
}) => {
  const { t } = useTranslation();
  const [selectedPal, setSelectedPal] = useState<LivePal | GearPal | null>(
    null
  );
  const [selectedZipCode, setSelectedZipCode] = useState<string | null>(null);
  const [showBackButton, setShowBackButton] = useState(false);

  // Raggruppa i GearPal per codice postale
  const gearPalsByZipCode = useMemo(() => {
    const grouped = new Map<string, GearPal[]>();
    pals.forEach((pal) => {
      if (pal.type === "GearPal") {
        const existing = grouped.get(pal.zipCode) || [];
        grouped.set(pal.zipCode, [...existing, pal]);
      }
    });
    return grouped;
  }, [pals]);

  return (
    <div className="h-full relative">
      <MapContainer center={[41.9028, 12.4964]} zoom={6} scrollWheelZoom={true}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {pals.map((pal, index) => {
          if (!pal.coordinates) return null;

          if (pal.type === "LivePal") {
            return (
              <Marker
                key={`${pal.userEmail}-${index}`}
                position={[pal.coordinates.lat, pal.coordinates.lng]}
                icon={livePalIcon}
                eventHandlers={{
                  click: () => {
                    setSelectedPal(pal);
                    setShowBackButton(false);
                  },
                }}
              />
            );
          } else {
            // Per i GearPal, mostra un marker speciale se ci sono più provider nello stesso CAP
            const gearPalsInZip = gearPalsByZipCode.get(pal.zipCode) || [];
            if (
              gearPalsInZip.length > 1 &&
              gearPalsInZip[0].userEmail === pal.userEmail
            ) {
              // Mostra solo un marker per CAP
              return (
                <GearPalClusterMarker
                  key={pal.zipCode}
                  position={[pal.coordinates.lat, pal.coordinates.lng]}
                  zipCode={pal.zipCode}
                  count={gearPalsInZip.length}
                  icon={gearPalClusterIcon}
                  onClick={() => setSelectedZipCode(pal.zipCode)}
                />
              );
            } else if (gearPalsInZip.length === 1) {
              // Se c'è solo un provider nel CAP, mostra il marker normale
              return (
                <Marker
                  key={`${pal.userEmail}-${index}`}
                  position={[pal.coordinates.lat, pal.coordinates.lng]}
                  icon={gearPalIcon}
                  eventHandlers={{
                    click: () => {
                      setSelectedPal(pal);
                      setShowBackButton(false);
                    },
                  }}
                />
              );
            }
          }
        })}
      </MapContainer>

      <MapLegend />

      {selectedZipCode && (
        <GearPalClusterPostalCodePopup
          zipCode={selectedZipCode}
          gearPals={gearPalsByZipCode.get(selectedZipCode) || []}
          onClose={() => setSelectedZipCode(null)}
          onSelectPal={(pal, showBack) => {
            setSelectedPal(pal);
            setShowBackButton(showBack || false);
          }}
          onPalUpdate={onPalUpdate}
        />
      )}

      {selectedPal && (
        <PalPopup
          pal={selectedPal}
          zIndex={showBackButton ? 1003 : 1001}
          onClose={() => {
            setSelectedPal(null);
            setShowBackButton(false);
          }}
          onBack={
            showBackButton
              ? () => {
                  setSelectedPal(null);
                  setShowBackButton(false);
                  // Non chiudiamo selectedZipCode, così torna alla lista
                }
              : undefined
          }
          onEditItems={
            selectedPal.type === "GearPal"
              ? () => {
                  // Quando l'utente fa clic su modifica, apriamo il form di modifica
                  onPalUpdate();
                  // Invia il GearPal attraverso l'evento personalizzato
                  const event = new CustomEvent("editGearPal", {
                    detail: {
                      ...selectedPal,
                    },
                  });
                  document.dispatchEvent(event);
                  setSelectedPal(null);
                  setShowBackButton(false);
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default MapWrapper;
